import { useMemo, useState, type ChangeEvent } from 'react';
import { Layout } from '@/components/layout/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { useSupportTickets } from '@/hooks/use-support-tickets';
import { Paperclip, Loader2, Upload, X } from 'lucide-react';
import { format } from 'date-fns';
import { tr } from 'date-fns/locale';

const statusConfig = {
  open: { label: 'Aciliyor', className: 'bg-blue-100 text-blue-700 border-blue-300' },
  working: { label: 'Calisiliyor', className: 'bg-amber-100 text-amber-700 border-amber-300' },
  completed: { label: 'Tamamlandi', className: 'bg-emerald-100 text-emerald-700 border-emerald-300' },
};

export default function Destek() {
  const { toast } = useToast();
  const { tickets, isLoading, createTicket, isCreatingTicket } = useSupportTickets();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [uploadProgress, setUploadProgress] = useState<{ uploaded: number; total: number } | null>(null);

  const sortedTickets = useMemo(
    () => [...tickets].sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()),
    [tickets]
  );

  const onFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(event.target.files || []);
    setFiles((prev) => [...prev, ...selectedFiles]);
    event.target.value = '';
  };

  const removeFile = (index: number) => {
    setFiles((prev) => prev.filter((_, currentIndex) => currentIndex !== index));
  };

  const handleCreateTicket = async () => {
    if (!title.trim() || !description.trim()) {
      toast({
        title: 'Eksik bilgi',
        description: 'Lutfen baslik ve aciklama alanlarini doldurun.',
        variant: 'destructive',
      });
      return;
    }

    try {
      setUploadProgress(files.length > 0 ? { uploaded: 0, total: files.length } : null);
      await createTicket({
        title,
        description,
        files,
        onUploadProgress: (uploaded, total) => setUploadProgress({ uploaded, total }),
      });

      setTitle('');
      setDescription('');
      setFiles([]);
      setUploadProgress(null);

      toast({
        title: 'Destek talebi olusturuldu',
        description: 'Talebiniz alindi. Durum degisiklikleri bu sayfada anlik guncellenecek.',
      });
    } catch (error: any) {
      console.error('Support ticket create error:', error);
      toast({
        title: 'Hata',
        description: error?.message || 'Destek talebi olusturulurken hata olustu.',
        variant: 'destructive',
      });
    }
  };

  return (
    <Layout>
      <div className="p-6 space-y-6">
        <div>
          <h1 className="text-3xl font-bold">Destek</h1>
          <p className="text-muted-foreground">Teknik veya operasyonel sorunlarinizi destek ekibine iletin.</p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Yeni Destek Talebi</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="support-title">Baslik</Label>
              <Input
                id="support-title"
                value={title}
                onChange={(event) => setTitle(event.target.value)}
                placeholder="Orn. Satis ekraninda odeme hatasi"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="support-description">Aciklama</Label>
              <Textarea
                id="support-description"
                value={description}
                onChange={(event) => setDescription(event.target.value)}
                rows={5}
                placeholder="Sorunu adim adim anlatin. Ne zaman oldu, nasil tekrar ediyor?"
              />
            </div>

            <div className="space-y-3">
              <Label>Ekran Goruntuleri</Label>
              <input
                type="file"
                id="support-files"
                className="hidden"
                multiple
                accept="image/png,image/jpeg,image/jpg,image/webp"
                onChange={onFileChange}
                disabled={isCreatingTicket}
              />
              <Button
                type="button"
                variant="outline"
                className="w-full"
                onClick={() => document.getElementById('support-files')?.click()}
                disabled={isCreatingTicket}
              >
                <Upload className="w-4 h-4 mr-2" />
                Ekran Goruntusu Ekle
              </Button>

              {files.length > 0 && (
                <div className="space-y-2">
                  {files.map((file, index) => (
                    <div key={`${file.name}-${index}`} className="flex items-center justify-between rounded-md border p-2">
                      <div className="flex items-center gap-2 min-w-0">
                        <Paperclip className="w-4 h-4 text-muted-foreground" />
                        <span className="text-sm truncate">{file.name}</span>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => removeFile(index)}
                        disabled={isCreatingTicket}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}

              <p className="text-xs text-muted-foreground">Yalnizca JPG/PNG/WebP, maksimum 5MB dosyalar.</p>
            </div>

            <div className="flex justify-end">
              <Button onClick={handleCreateTicket} disabled={isCreatingTicket}>
                {isCreatingTicket ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {uploadProgress ? `Yukleniyor (${uploadProgress.uploaded}/${uploadProgress.total})` : 'Olusturuluyor'}
                  </>
                ) : (
                  'Destek Talebi Olustur'
                )}
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Destek Taleplerim</CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="py-10 text-center text-muted-foreground">Destek talepleri yukleniyor...</div>
            ) : sortedTickets.length === 0 ? (
              <div className="py-10 text-center text-muted-foreground">Henuz destek talebi olusturmadiniz.</div>
            ) : (
              <div className="space-y-3">
                {sortedTickets.map((ticket) => {
                  const cfg = statusConfig[ticket.status];
                  return (
                    <div key={ticket.id} className="rounded-lg border p-4 space-y-3">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <h3 className="font-semibold">{ticket.title}</h3>
                          <p className="text-sm text-muted-foreground">
                            {format(new Date(ticket.created_at), 'PPp', { locale: tr })}
                          </p>
                        </div>
                        <Badge variant="outline" className={cfg.className}>
                          {cfg.label}
                        </Badge>
                      </div>

                      <p className="text-sm">{ticket.description}</p>

                      {(ticket.attachments || []).length > 0 && (
                        <div className="space-y-2">
                          <p className="text-sm font-medium">Eklenen ekran goruntuleri</p>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                            {(ticket.attachments || []).map((attachment) => (
                              <a
                                key={attachment.id}
                                href={attachment.public_url}
                                target="_blank"
                                rel="noreferrer"
                                className="border rounded-md overflow-hidden"
                              >
                                <img
                                  src={attachment.public_url}
                                  alt={attachment.file_name}
                                  className="w-full h-28 object-cover"
                                />
                              </a>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
